import React, { useState } from "react";

import { Check, Circle, Copy, Eye, EyeOff, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/src/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import { Skeleton } from "@/src/components/ui/skeleton";
import { cn } from "@/src/lib/utils";

import { maskKey } from "./liveFormat";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";

import type { JSX } from "react";

import type { LiveView } from "../../utils/liveView";

interface LiveConnectionCardProps {
  view: LiveView;
}

type StepStatus = "done" | "current" | "pending" | "error";

interface ChecklistStep {
  key: string;
  status: StepStatus;
  owner: "okast" | "obs";
}

interface ValueRowProps {
  label: string;
  display: string | null;
  isLoading: boolean;
  isMissing: boolean;
  actions?: JSX.Element;
}

const STEP_STATUSES: Partial<Record<string, StepStatus[]>> = {
  provisioning: ["current", "pending", "pending"],
  provisioningFailed: ["error", "pending", "pending"],
  waitingSignal: ["done", "current", "pending"],
  checkingSignal: ["done", "done", "current"],
  readyToAir: ["done", "done", "done"],
  publishing: ["done", "done", "done"],
};

const DEFAULT_STATUSES: StepStatus[] = ["done", "pending", "pending"];

const STEP_KEYS: Pick<ChecklistStep, "key" | "owner">[] = [
  { key: "generated", owner: "okast" },
  { key: "signal", owner: "obs" },
  { key: "quality", owner: "okast" },
];

const DESCRIPTION_KEYS: Partial<Record<string, string>> = {
  provisioning: "liveRegie_connectionDescriptionLoading",
  provisioningFailed: "liveRegie_connectionDescriptionFailed",
};

function getDisplayedKey(
  streamKey: string | null,
  isVisible: boolean,
): string | null {
  if (streamKey === null) return null;
  return isVisible ? streamKey : maskKey(streamKey);
}

const STEP_VARIANT: Record<StepStatus, string> = {
  done: "done",
  current: "todo",
  pending: "todo",
  error: "error",
};

function getSteps(state: string): ChecklistStep[] {
  const statuses = STEP_STATUSES[state] ?? DEFAULT_STATUSES;
  return STEP_KEYS.map(({ key, owner }, index) => ({
    key,
    owner,
    status: statuses[index] ?? "pending",
  }));
}

interface StepIconProps {
  status: StepStatus;
}

function StepIcon({ status }: StepIconProps): JSX.Element {
  if (status === "done") {
    return <Check aria-hidden="true" className="text-status-active size-4" />;
  }
  if (status === "current") {
    return <Loader2 aria-hidden="true" className="size-4 animate-spin" />;
  }
  if (status === "error") {
    return <X aria-hidden="true" className="text-status-error size-4" />;
  }
  return <Circle aria-hidden="true" className="text-muted-foreground size-4" />;
}

function ValueRow({
  label,
  display,
  isLoading,
  isMissing,
  actions,
}: ValueRowProps): JSX.Element {
  const { t } = useTranslation("editor-content");

  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs">{label}</span>
      <div className="bg-muted/50 flex items-center gap-1 rounded-md border px-2 py-1.5">
        {isLoading ? (
          <Skeleton className="h-5 flex-1" />
        ) : (
          <span
            className={cn(
              "min-w-0 flex-1 truncate font-mono text-sm",
              isMissing && "text-muted-foreground font-sans",
            )}
          >
            {isMissing ? t("liveRegie_notReceived") : display}
          </span>
        )}
        {actions}
      </div>
    </div>
  );
}

function LiveConnectionCard({ view }: LiveConnectionCardProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const copy = useCopyToClipboard();
  const [isKeyVisible, setIsKeyVisible] = useState(false);
  const { state, streamUrl, streamKey, phase } = view;
  const isLoading = state === "provisioning" && !streamUrl && !streamKey;
  const isFailed = state === "provisioningFailed";
  const isUrlMissing = isFailed && streamUrl === null;
  const isKeyMissing = isFailed && streamKey === null;
  const hasKey = streamKey !== null;

  function renderCopyButton(value: string | null, label: string): JSX.Element {
    return (
      <Button
        aria-label={label}
        className="size-7"
        disabled={value === null}
        size="icon"
        variant="ghost"
        onClick={() => {
          if (value !== null) copy({ value }).catch(() => undefined);
        }}
      >
        <Copy aria-hidden="true" />
      </Button>
    );
  }

  const description = t(
    DESCRIPTION_KEYS[state] ?? "liveRegie_connectionDescription",
  );
  const displayedKey = getDisplayedKey(streamKey, isKeyVisible);

  return (
    <Card className="gap-4 py-4">
      <CardHeader className="px-4">
        <CardTitle>{t("liveRegie_connectionTitle")}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 px-4">
        {phase === "antenne" && state === "signalLost" ? (
          <p className="text-muted-foreground text-xs">
            {t("liveRegie_sameCredentials")}
          </p>
        ) : null}
        <ValueRow
          actions={renderCopyButton(streamUrl, t("liveRegie_copyUrl"))}
          display={streamUrl}
          isLoading={isLoading}
          isMissing={isUrlMissing}
          label={t("liveRegie_serverUrl")}
        />
        <ValueRow
          display={displayedKey}
          isLoading={isLoading}
          isMissing={isKeyMissing}
          label={t("liveRegie_streamKey")}
          actions={
            <React.Fragment>
              <Button
                aria-pressed={isKeyVisible}
                className="size-7"
                disabled={!hasKey}
                onClick={() => setIsKeyVisible((visible) => !visible)}
                size="icon"
                variant="ghost"
                aria-label={t(
                  isKeyVisible ? "liveRegie_hideKey" : "liveRegie_showKey",
                )}
              >
                {isKeyVisible ? (
                  <EyeOff aria-hidden="true" />
                ) : (
                  <Eye aria-hidden="true" />
                )}
              </Button>
              {renderCopyButton(streamKey, t("liveRegie_copyKey"))}
            </React.Fragment>
          }
        />
        {phase === "connexion" ? (
          <ul className="flex flex-col gap-2 border-t pt-3">
            {getSteps(state).map((step) => (
              <li key={step.key} className="flex items-center gap-2 text-sm">
                <StepIcon status={step.status} />
                <span
                  className={cn(
                    step.status === "pending" && "text-muted-foreground",
                  )}
                >
                  {t(`liveRegie_step_${step.key}_${STEP_VARIANT[step.status]}`)}
                </span>
                <span className="text-muted-foreground ml-auto text-xs">
                  {t(`liveRegie_owner_${step.owner}`)}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default LiveConnectionCard;
